import { Observable, of } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  map,
  take,
  timeout
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { DocumentType } from 'src/app/features/document/document'
import { documentActions } from 'src/app/features/document/document.actions'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { OnboardingSlide } from 'src/app/shared/interfaces'
import { environment } from 'src/environments/environment'
import { v4 as uuidv4 } from 'uuid'

import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core'
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { configurationOptionSelectors } from '../../configuration-option.selectors'
import { ConfigurationOptionService } from '../../configuration-option.service'
import { scrollToFirstInvalidControl } from 'src/app/shared/until'

@Component({
  selector: 'configuration-option-onboarding-card',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class ConfigurationOptionOnboardingComponent implements OnInit {
  @Output() onSubmit = new EventEmitter<any>()
  public formValues: any
  public formValues$: Observable<any>
  public isLoading$: Observable<boolean>
  public isCollapsed = false
  public accessRights = AccessRights

  public staticAssetBaseUrl = environment.staticAssetBaseUrl

  public form: UntypedFormGroup
  public availableLanguages = environment.frontendLanguages.reduce(
    (acc, curr) => {
      if (curr != 'xx') return (acc[curr] = true), acc
      else return acc
    },
    {}
  )

  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private configurationOptionService: ConfigurationOptionService,
    private notificationService: NotificationService,
    private actions$: Actions,
    private elementRef: ElementRef
  ) {}

  get slides() {
    return this.form.get('slides') as UntypedFormArray
  }

  ngOnInit() {
    this.form = this.fb.group({
      slides: this.fb.array([])
    })

    this.isLoading$ = this.store.select(
      configurationOptionSelectors.selectIsLoading
    )

    this.formValues$ = this.store
      .select(configurationOptionSelectors.selectAll)
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
        map((configOptions) =>
          this.configurationOptionService.mapEntitiesToObject(configOptions)
        )
      )

    this.formValues$.pipe(take(1)).subscribe((formValues) => {
      if (!formValues?.onboarding_slides) {
        return
      }

      const slides = JSON.parse(formValues.onboarding_slides)

      for (const slide of slides) {
        this.addSlide(slide)
      }
    })
  }

  public addSlide(slide?: OnboardingSlide) {
    this.slides.push(this.createSlide(slide))
  }

  public removeSlide(index: number) {
    this.slides.removeAt(index)
  }

  private createSlide(slide?: OnboardingSlide): UntypedFormGroup {
    return this.fb.group({
      imagePath: [slide?.imagePath ?? null, Validators.required],
      en: this.fb.group({
        title: [
          slide?.en?.title ?? null,
          this.availableLanguages['en'] ? [Validators.required] : []
        ],
        description: [
          slide?.en?.description ?? null,
          this.availableLanguages['en'] ? [Validators.required] : []
        ]
      }),
      nl: this.fb.group({
        title: [
          slide?.nl?.title ?? null,
          this.availableLanguages['nl'] ? [Validators.required] : []
        ],
        description: [
          slide?.nl?.description ?? null,
          this.availableLanguages['nl'] ? [Validators.required] : []
        ]
      }),
      de: this.fb.group({
        title: [
          slide?.de?.title ?? null,
          this.availableLanguages['de'] ? [Validators.required] : []
        ],
        description: [
          slide?.de?.description ?? null,
          this.availableLanguages['de'] ? [Validators.required] : []
        ]
      }),
      fr: this.fb.group({
        title: [
          slide?.fr?.title ?? null,
          this.availableLanguages['fr'] ? [Validators.required] : []
        ],
        description: [
          slide?.fr?.description ?? null,
          this.availableLanguages['fr'] ? [Validators.required] : []
        ]
      })
    })
  }

  public getSlides(form) {
    return form.controls.slides.controls
  }

  public onSubmitImage(file: File, index: number) {
    const path = `branding/${uuidv4()}`
    this.store.dispatch(
      documentActions.createOneWithFile({
        entity: {
          type: DocumentType.BRANDING_IMAGE,
          isPublic: true,
          path
        },
        file
      })
    )

    this.actions$
      .pipe(
        ofType(documentActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (!action) {
          return this.notificationService.emit('alerts.warning', 'error')
        }

        const formGroup = this.slides.controls[index] as UntypedFormGroup
        formGroup.get('imagePath').setValue(path)
      })
  }

  public onFormSubmit(): void {
    if (!this.form.valid) {
      const slideWithMissingImage = this.form.value.slides.find(
        (slide) => !slide.imagePath?.length
      )
      if (Boolean(slideWithMissingImage)) {
        this.notificationService.emit('Missing image', 'warning')
      }

      for (let i = 0; i < this.slides.length; i++) {
        const formGroup = this.slides.controls[i] as UntypedFormGroup
        for (const lang of Object.keys(this.availableLanguages)) {
          const langGroup = formGroup.controls[lang] as UntypedFormGroup
          Object.keys(langGroup.controls).forEach((key) => {
            langGroup.controls[key].markAsDirty()
          })
        }
      }
      scrollToFirstInvalidControl(
        this.elementRef,
        slideWithMissingImage ? null : this.notificationService,
        'form [aria-invalid="true"]'
      )
      return
    }

    this.configurationOptionService.updateOrCreateMany({
      onboarding_slides: JSON.stringify(this.form.value.slides)
    })

    this.notificationService.emit('alerts.success', 'success')
  }
}
